import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, NotificationsActive } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  createReminder,
  deleteReminder,
  getTaskReminders,
} from '../../../../../services/reminderService';

// The email goes out this long before the moment the user picks. Mirrors
// REMINDER_LEAD_MINUTES on the backend.
const LEAD_MINUTES = 60;

const STATUS_LABEL = {
  sent: 'Sent',
  missed: 'Missed',
  skipped: 'Skipped',
};

const TaskReminders = ({ taskId }) => {
  const [reminders, setReminders] = useState([]);
  const [remindAt, setRemindAt] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchReminders = useCallback(async () => {
    if (!taskId) return;
    try {
      const response = await getTaskReminders(taskId);
      setReminders(response?.data?.data || []);
    } catch (error) {
      // A failed fetch must not break the drawer; the rest of the task still works.
      setReminders([]);
    }
  }, [taskId]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleAdd = async () => {
    const trimmedNote = note.trim();
    if (!remindAt || !remindAt.isValid?.() || !trimmedNote) return;
    try {
      setSaving(true);
      // Send an absolute instant: the browser resolves its own offset so the server never
      // has to guess which timezone "15:00" meant.
      const response = await createReminder({
        taskId,
        remindAt: remindAt.toISOString(),
        note: trimmedNote,
      });
      toast.success(response?.data?.message);
      setRemindAt(null);
      setNote('');
      await fetchReminders();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not add the reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      const response = await deleteReminder(reminderId);
      toast.success(response?.data?.message);
      await fetchReminders();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not delete the reminder');
    }
  };

  const pending = reminders.filter((reminder) => reminder.status === 'pending');
  const past = reminders.filter((reminder) => reminder.status !== 'pending');

  const renderRow = (reminder, isPast) => (
    <Stack
      key={reminder.id}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ py: 0.5, opacity: isPast ? 0.55 : 1 }}
    >
      <NotificationsActive fontSize="small" sx={{ color: isPast ? 'text.disabled' : 'primary.main' }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={reminder.note}>
          {reminder.note}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {dayjs(reminder.remindAt).format('DD MMM YYYY, HH:mm')}
          {isPast
            ? ` · ${STATUS_LABEL[reminder.status] || reminder.status}${
                reminder.sentTo?.userName ? ` to ${reminder.sentTo.userName}` : ''
              }`
            : ` · email ${LEAD_MINUTES} min before`}
        </Typography>
      </Box>
      <Tooltip title="Delete reminder" placement="top" arrow>
        <IconButton size="small" onClick={() => handleDelete(reminder.id)}>
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Reminders
      </Typography>

      {/* One row: picker, note, Add. The button is pinned to 40px so it lines up with the
          small text fields rather than sitting taller than them. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Date & time"
            value={remindAt}
            onChange={(value) => setRemindAt(value)}
            minDateTime={dayjs()}
            inputFormat="DD MMM YYYY, HH:mm"
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ minWidth: 220 }} />
            )}
          />
        </LocalizationProvider>
        <TextField
          size="small"
          fullWidth
          placeholder="What is it about?"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={!remindAt || !remindAt.isValid?.() || !note.trim() || saving}
          sx={{ height: 40, flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Add
        </Button>
      </Stack>

      {pending.map((reminder) => renderRow(reminder, false))}
      {past.map((reminder) => renderRow(reminder, true))}

      {!reminders.length && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          No reminders yet. The assignee is emailed {LEAD_MINUTES} minutes before the time you set.
        </Typography>
      )}
    </Box>
  );
};

TaskReminders.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TaskReminders;
